'use client';

import { differenceInDays } from 'date-fns';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useDialogs } from '@/components/dialogs/hooks';
import { CookieType } from '@/helpers/common';
import { checkIfLootboxActiveById } from '@/helpers/realTimeService';
import { groupMemberFiatPlayers, groupMemberVerified } from '@/helpers/user';
import { useInitStore } from '@/hooks';
import { useCustomerIO } from '@/hooks/3dParty/customerIO';
import { useHotjar, useHotjarStagEvent } from '@/hooks/3dParty/hotjar';
import { useIntercom } from '@/hooks/3dParty/intercom';
import { useOneSignalPush } from '@/hooks/3dParty/useOneSignal';
import { pickProperties, useCookie } from '@/hooks/common';
import useEstonia from '@/hooks/estonia';
import { useGamesFiltration } from '@/hooks/gamesFiltration';
import { useSyncABTestsUserGroup } from '@/modules/abTesting';
import {
  useLoyaltySSESubscription,
  useNDBConfirmEmailModals,
  usePrefilledNdbCouponSync,
} from '@/modules/common';
import { useAuthCookies } from '@/modules/common/hooks/useAuthCookies';
import { usePwaOpened } from '@/modules/googleAnalytics/hooks/usePwaOpened';
import { useSetupUserLanguage } from '@/modules/localization/hooks/useSetupUserLanguage';
import { updateBonanzaData } from '@/modules/promotions';
import { useNotifyBonusesOnReturn } from '@/modules/promotions/bonusNotification';
import { useGlobalRealTimeSubscriptions } from '@/modules/realTime/hooks';
import { STORAGE, useGetCommonStore, useLocalStorageStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';

const USER_REFRESH_INTERVAL = 60e3;
const CONFIRMATION_EMAIL_ROUTE = '/users/confirmation';
const SHOW_EMAIL_POPUP_AGAIN_AFTER_DAYS = 7;

interface StoreClientProps {
  language: string;
  isActiveSessionBackend: boolean;
}

export const StoreClient = ({
  language,
  isActiveSessionBackend,
}: StoreClientProps) => {
  const { initCustomerIO, resetCustomerIO } = useCustomerIO();

  const { isEstonia } = useEstonia();
  const useCommonStore = useGetCommonStore();

  const countryCode = useCommonStore.use.countryCode();
  const { modal } = useDialogs();

  const pathname = usePathname();
  const {
    isAuthenticated,
    pendingUser,
    user,
    fetchUser,
    refreshUser,
    fetchFavoriteGames,
    setCountryCodeData,
    setActiveJackpot,
    fetchLootboxes,
    fetchBonuses,
    fetchFreespins,
    fetchRecentGamesIds,
    fetchUserTournamentStatus,
    withoutTagsLootboxes,
    activeLootboxId,
    dismissedLootboxId,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      pendingUser: state.pendingUser,
      user: state.user,
      fetchUser: state.fetchUser,
      refreshUser: state.refreshUser,
      fetchFavoriteGames: state.fetchFavoriteGames,
      setCountryCodeData: state.setCountryCodeData,
      setActiveJackpot: state.setActiveJackpot,
      fetchLootboxes: state.fetchLootboxes,
      fetchBonuses: state.fetchBonuses,
      fetchFreespins: state.fetchFreespins,
      fetchRecentGamesIds: state.fetchRecentGamesIds,
      fetchUserTournamentStatus: state.fetchUserTournamentStatus,
      withoutTagsLootboxes: state.withoutTagsLootboxes,
      activeLootboxId: state.activeLootboxId,
      dismissedLootboxId: state.dismissedLootboxId,
    }))
  );

  useInitStore();

  useEffect(() => {
    setCountryCodeData(isEstonia);
  }, [isEstonia, user, setCountryCodeData]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchFavoriteGames();
  }, [fetchFavoriteGames, isAuthenticated]);

  useLoyaltySSESubscription();

  useEffect(() => {
    updateBonanzaData(useCommonStore.setState, language, user);
    useCommonStore.setState({ isBonanzaInitialized: true });
  }, [language, user, useCommonStore]);

  useEffect(() => {
    // TODO: we need to refactor fetchUsers, because it not only
    // fetches the user, it also fetches other values (accounts, compoints)
    fetchUser(REASON_FOR_UPDATING_USER.VISIT, isActiveSessionBackend);
  }, [fetchUser, isActiveSessionBackend]);

  useEffect(() => {
    if (!user || groupMemberVerified(user)) {
      return useCommonStore.setState({ isVerifyMessage: false });
    }

    if (user && groupMemberFiatPlayers(user)) {
      useCommonStore.setState({
        isVerifyMessage: true,
      });
    }
  }, [user, useCommonStore.setState]);

  useGlobalRealTimeSubscriptions();

  useNotifyBonusesOnReturn();

  useGamesFiltration();

  useEffect(() => {
    initCustomerIO();
    return () => resetCustomerIO();
  }, [initCustomerIO, resetCustomerIO]);

  useIntercom();

  usePwaOpened();

  useOneSignalPush({ userId: user?.id, email: user?.email });

  useHotjar();

  useHotjarStagEvent();

  useSyncABTestsUserGroup();

  useAuthCookies();

  useCookie({
    name: 'userDetails',
    value: {
      type: CookieType.Object,
      data: pickProperties(user, [
        'id',
        'email',
        'statuses',
        'language',
        'country',
      ]),
    },
    days: 90,
    encryptKey: 'QExMyj2jKBnWDGpv',
    withReplace: true,
    active: !!user?.id,
  });

  useEffect(() => {
    if (
      !isAuthenticated ||
      pendingUser ||
      user?.confirmedAt ||
      pathname === CONFIRMATION_EMAIL_ROUTE
    ) {
      useCommonStore.setState({ isEmailConfirmationPopup: false });

      if (user?.confirmedAt || pathname === CONFIRMATION_EMAIL_ROUTE) {
        localStorage.removeItem(STORAGE.LAST_SEEN_EMAIL_CONFIRMATION_POPUP);
      }
    }

    if (isAuthenticated && !user?.confirmedAt) {
      const lastSeenEmailConfirmationPopup = useLocalStorageStore
        .getState()
        .get(STORAGE.LAST_SEEN_EMAIL_CONFIRMATION_POPUP, null, true);

      const lastSeenDate =
        typeof lastSeenEmailConfirmationPopup === 'string' ||
        typeof lastSeenEmailConfirmationPopup === 'number'
          ? new Date(lastSeenEmailConfirmationPopup)
          : null;

      const isEmailConfirmationPopup =
        !lastSeenDate ||
        differenceInDays(new Date(), lastSeenDate) >=
          SHOW_EMAIL_POPUP_AGAIN_AFTER_DAYS;

      useCommonStore.setState({
        isEmailConfirmationPopup,
      });
    }
  }, [isAuthenticated, pendingUser, user, countryCode, pathname]);

  useNDBConfirmEmailModals();
  usePrefilledNdbCouponSync();

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => refreshUser(), USER_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUser, modal]);

  useEffect(() => {
    setActiveJackpot();
  }, [setActiveJackpot]);

  useEffect(() => {
    if (!user?.id) return;

    fetchBonuses();
    fetchFreespins();
    fetchRecentGamesIds();
    fetchLootboxes();
    fetchUserTournamentStatus();
  }, [
    fetchLootboxes,
    fetchBonuses,
    fetchFreespins,
    fetchRecentGamesIds,
    fetchUserTournamentStatus,
    user?.id,
  ]);

  useEffect(() => {
    const lastLootbox = withoutTagsLootboxes
      .filter((lootbox) => lootbox.live)
      .pop();

    if (lastLootbox) {
      const isActive = checkIfLootboxActiveById(
        withoutTagsLootboxes,
        lastLootbox.id
      );

      !activeLootboxId &&
        lastLootbox.id !== dismissedLootboxId &&
        isActive &&
        useCommonStore.setState({ activeLootboxId: lastLootbox.id });
    }
  }, [activeLootboxId, dismissedLootboxId, withoutTagsLootboxes]);

  useSetupUserLanguage(language);

  return null;
};
